// Browser side of the provably fair protocol (docs/REILUUS.md). The player's secret seed is
// created here with Web Crypto and kept in this browser; only its commitment is sent when
// joining. After the tournament starts, the seed is revealed automatically.
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.ts';
import { fromHex, isSeedHex, playerCommitment, toHex } from '../../shared/fairness.ts';
import type { Card } from '../../shared/cards.ts';
/**
 * Whether the fairness details (badge, tournament section, hand verifier) are shown. Hidden for
 * now at the owner's request; the protocol itself keeps running (commitments at join, seeds
 * revealed automatically, evidence published), so turning this on shows full records.
 */
export const SHOW_FAIRNESS = false;


interface StoredSeed {
  seed: string;
  commitment: string;
  /** Set when the server confirmed the reveal: lets the verifier notice a seed "lost" by the server. */
  revealedAt?: number;
}

const seedKey = (tournamentId: string, userId: string) => `uh.fair.${tournamentId}.${userId}`;

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable (private mode): the seed simply cannot be revealed from this device
  }
}

export function storedSeed(tournamentId: string, userId: string): StoredSeed | null {
  const s = read<StoredSeed>(seedKey(tournamentId, userId));
  return s && isSeedHex(s.seed) && isSeedHex(s.commitment) ? s : null;
}

/**
 * The commitment to send when joining. The seed is created once per tournament and user and
 * reused on retries, so a lost response never leaves the server with a commitment this browser
 * cannot open.
 */
export function commitmentForJoin(tournamentId: string, userId: string): string {
  const existing = storedSeed(tournamentId, userId);
  if (existing) return existing.commitment;
  const seed = new Uint8Array(32);
  crypto.getRandomValues(seed);
  const entry: StoredSeed = { seed: toHex(seed), commitment: toHex(playerCommitment(tournamentId, userId, seed)) };
  write(seedKey(tournamentId, userId), entry);
  return entry.commitment;
}

/** Reveals this device's seeds for tournaments that are waiting for them. Mounted once in the app shell. */
export function useAutoReveal(userId: string | undefined): void {
  const qc = useQueryClient();
  const pending = useQuery({
    queryKey: ['fairness-pending'],
    queryFn: () => api.get<{ pending: { tournamentId: string; deadlineAt: number | null; commitment: string }[] }>('/api/me/fairness/pending'),
    enabled: !!userId,
    refetchInterval: 60_000,
  });
  useEffect(() => {
    if (!userId) return;
    for (const p of pending.data?.pending ?? []) {
      const s = storedSeed(p.tournamentId, userId);
      // Only the seed behind the commitment the server holds (another device may have joined).
      if (!s || s.commitment !== p.commitment) continue;
      // The commitment must still be ours; never send a seed that does not open it.
      if (toHex(playerCommitment(p.tournamentId, userId, fromHex(s.seed))) !== s.commitment) continue;
      void api
        .post<{ status: string }>(`/api/tournaments/${p.tournamentId}/fairness/reveal`, { seed: s.seed })
        .then(() => {
          write(seedKey(p.tournamentId, userId), { ...s, revealedAt: Date.now() });
          void qc.invalidateQueries({ queryKey: ['table', p.tournamentId] });
          void qc.invalidateQueries({ queryKey: ['fairness', p.tournamentId] });
        })
        .catch(() => undefined);
    }
  }, [pending.data, userId, qc]);
}

// ---------------------------------------------------------------------------
// What this device saw during a hand, for the verifier to compare against later.

export interface Observed {
  seat: number;
  hole: Card[];
  board: Card[];
}

const seenKey = (tournamentId: string, userId: string, handNo: number) => `uh.seen.${tournamentId}.${userId}.${handNo}`;

export function recordObserved(tournamentId: string, userId: string, handNo: number, obs: Observed): void {
  const prev = read<Observed>(seenKey(tournamentId, userId, handNo));
  if (prev && prev.board.length >= obs.board.length && prev.hole.length >= obs.hole.length) return;
  write(seenKey(tournamentId, userId, handNo), obs);
}

export function observedFor(tournamentId: string, userId: string, handNo: number): Observed | null {
  return read<Observed>(seenKey(tournamentId, userId, handNo));
}
